import React, { useState, useEffect } from 'react';
import { Music, Pause, Play } from 'lucide-react';
import WidgetContainer from '../WidgetContainer';

const NowPlaying = ({ delay = 0 }) => {
  const [spotifyData, setSpotifyData] = useState(null);
  const [localProgress, setLocalProgress] = useState(0);
  const [lastFetchTime, setLastFetchTime] = useState(Date.now());

  // Fetch Spotify data every 2 seconds
  useEffect(() => {
    const fetchSpotifyData = async () => {
      try {
        const response = await fetch('https://spotify.sillydev.lol/current');
        const data = await response.json();
        
        // Check if there's an actual song playing
        if (data.playing && data.track_name) {
          setSpotifyData(data);
          setLocalProgress(data.progress_ms);
          setLastFetchTime(Date.now());
        } else {
          // Set offline state
          setSpotifyData({ playing: false, offline: true });
        }
      } catch (error) {
        console.error('Failed to fetch Spotify data:', error);
        // Set offline state on error
        setSpotifyData({ playing: false, offline: true });
      }
    };

    fetchSpotifyData();
    const fetchInterval = setInterval(fetchSpotifyData, 2000);
    return () => clearInterval(fetchInterval);
  }, []);

  // Update progress locally every 100ms for smooth animation
  useEffect(() => {
    if (!spotifyData || !spotifyData.playing) return;

    const progressInterval = setInterval(() => {
      const elapsedSinceFetch = Date.now() - lastFetchTime;
      const newProgress = localProgress + elapsedSinceFetch;
      
      if (newProgress < spotifyData.duration_ms) {
        setLocalProgress(localProgress + 100);
      }
    }, 100);

    return () => clearInterval(progressInterval);
  }, [spotifyData, localProgress, lastFetchTime]);

  if (!spotifyData) {
    return (
      <WidgetContainer size="1x2" delay={delay}>
        <div className="widget-header">
          <h3>Now Playing</h3>
          <Music className="widget-icon animate-pulse" />
        </div>
        <div className="flex gap-4 mb-4">
          <div className="skeleton w-24 h-24" />
          <div className="flex-1 space-y-2">
            <div className="skeleton h-3 w-3/4" />
            <div className="skeleton h-3 w-1/2" />
            <div className="skeleton h-3 w-2/3" />
          </div>
        </div>
        <div className="skeleton h-2 w-full" />
      </WidgetContainer>
    );
  }

  // Show offline state
  if (spotifyData.offline || !spotifyData.playing) {
    return (
      <WidgetContainer size="1x2" delay={delay}>
        <div className="widget-header">
          <h3>Now Playing</h3>
          <Pause className="widget-icon" />
        </div>

        <div className="flex flex-col items-center justify-center h-32 text-center">
          <Music className="w-12 h-12 text-coffee-400 mb-3 opacity-50" />
          <div className="text-sm font-medium text-coffee-300">Offline</div>
          <div className="text-xs text-coffee-400/80 mt-1">No music playing</div>
        </div>
      </WidgetContainer>
    );
  }

  const progressPercentage = (localProgress / spotifyData.duration_ms) * 100;
  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <WidgetContainer size="1x2" delay={delay}>
      {/* Blurred album-art backdrop */}
      <div
        className="absolute inset-0 -z-10 opacity-40 pointer-events-none rounded-3xl overflow-hidden"
        style={{
          backgroundImage: `url(${spotifyData.album_art})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(36px) saturate(1.4)',
        }}
      />
      <div className="absolute inset-0 -z-10 rounded-3xl bg-espresso-900/70 pointer-events-none" />

      <div className="widget-header">
        <h3>Now Playing</h3>
        {spotifyData.playing ? (
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" style={{ boxShadow: '0 0 8px rgba(52,211,153,0.6)' }}></span>
            <Music className="widget-icon" />
          </div>
        ) : (
          <Pause className="widget-icon" />
        )}
      </div>

      <div className="flex gap-4 mb-4">
        {/* Album Art */}
        <div className="relative flex-shrink-0">
          <img
            src={spotifyData.album_art}
            alt={spotifyData.album}
            className="w-24 h-24 rounded-xl shadow-2xl ring-1 ring-coffee-600/40"
            style={{
              boxShadow: spotifyData.playing
                ? '0 8px 32px rgba(0,0,0,0.5), 0 0 24px rgba(255, 232, 200, 0.15)'
                : '0 8px 32px rgba(0,0,0,0.5)',
            }}
          />
          {spotifyData.playing && (
            <span
              className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-caramel-500 flex items-center justify-center ring-2 ring-espresso-900"
              aria-hidden
            >
              <Play className="w-2.5 h-2.5 text-espresso-900 fill-espresso-900" />
            </span>
          )}
        </div>

        {/* Track Info */}
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div className="text-sm font-semibold text-cream-100 truncate">
            {spotifyData.track_name}
          </div>
          <div className="text-xs text-cream-200/80 truncate">
            {spotifyData.artist}
          </div>
          <div className="text-xs text-coffee-300 truncate mt-1">
            {spotifyData.album}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${progressPercentage}%` }}
        ></div>
      </div>
      <div className="flex justify-between text-xs text-coffee-300 mt-1">
        <span className="font-mono">{formatTime(localProgress)}</span>
        <span className="font-mono">{formatTime(spotifyData.duration_ms)}</span>
      </div>
    </WidgetContainer>
  );
};

export default NowPlaying;
